/**
 * Fire-brigade truck gag: clicking the burning car summons this truck. It rolls
 * in from off-screen left, parks just shy of the car and blasts water from a
 * deck monitor. Spraying continues indefinitely until the world tells it to
 * `'leave'` (modal close / confirm) — so the truck stays on scene the whole
 * time the upload modal is open.
 *
 * The truck publishes its `sprayAmount()` (0/1); the car reads that each frame
 * via `setDousing` (objects.ts) and scales its flames down accordingly — so the
 * fire visibly shrinks under the water jet and pops back once the truck leaves
 * (if the user cancelled without uploading).
 *
 * Container sits at the car's y so the wheels and bodylines share the street.
 * Imperative API: `command('extinguish' | 'leave' | 'idle')`.
 */
import { Container, Graphics } from 'pixi.js'
import { PALETTE as P } from './palette'
import { rect, circle } from './draw'
import { makeOutline } from './objects'

const DRIVE_IN_MS = 700
const DRIVE_OUT_MS = 700

const OFFSCREEN_X = -96
const PARK_X = 4
const TRUCK_Y = 184 // matches the car's y so the wheels line up with the street

// Where the water lands in world coords (the burning car, roughly its centre).
const TARGET_WX = 151
const TARGET_WY = 200

// Hose nozzle position in container-local coords (76 wide truck art).
const HOSE_LX = 54
const HOSE_LY = 4

type State = 'offscreen' | 'driving-in' | 'spraying' | 'driving-out'

export interface FireTruck {
  container: Container
  /**
   * `extinguish` kicks off the drive-in (no-op if already on scene); `leave`
   * tells a parked truck to drive back out; `idle` is a hard reset
   * (snaps off-screen, used on world destroy).
   */
  command(mode: 'idle' | 'extinguish' | 'leave'): void
  /** 0 (not spraying) → 1 (full spray). Read by the car to scale flames down. */
  sprayAmount(): number
  update(dtMs: number, timeMs: number): void
}

export function createFireTruck(): FireTruck {
  const container = new Container()
  container.label = 'firetruck'

  // Body has the shared black silhouette outline; water doesn't, so droplets
  // stay crisp at 1–2 px.
  const body = new Graphics()
  body.filters = [makeOutline()]
  container.addChild(body)
  const water = new Graphics()
  container.addChild(water)

  let state: State = 'offscreen'
  let elapsed = 0
  let x = OFFSCREEN_X
  container.position.set(x, TRUCK_Y)
  container.alpha = 0

  function drawBody(g: Graphics, timeMs: number, deployed: boolean): void {
    g.clear()
    // ─── Tank (left) + cab (right) ─────────────────────────────────────────
    rect(g, 4, 14, 50, 18, P.carBody)
    rect(g, 4, 14, 50, 2, P.carBodyLight)
    rect(g, 4, 30, 50, 2, P.carBodyDark)
    rect(g, 53, 8, 23, 24, P.carBody)
    rect(g, 53, 8, 23, 2, P.carBodyLight)
    rect(g, 53, 30, 23, 2, P.carBodyDark)
    // Cab window + door seam
    rect(g, 56, 12, 12, 9, P.carGlass)
    rect(g, 56, 12, 12, 2, P.glassSheen, 0.55)
    rect(g, 67, 18, 1, 13, P.carBodyDark, 0.6)
    // Front bumper, headlight, grille
    rect(g, 74, 26, 3, 5, 0xc2c8ce)
    rect(g, 72, 22, 3, 3, P.carHeadlight)
    rect(g, 71, 28, 5, 1, P.carGrille)
    // Rear taillight
    rect(g, 2, 20, 3, 5, P.carTaillight)
    // White panel + red cross on the tank — recognisably an emergency vehicle
    rect(g, 14, 19, 28, 7, P.signBoard)
    rect(g, 14, 19, 28, 1, 0xd8d0bf)
    rect(g, 27, 20, 2, 5, P.fireDeep)
    rect(g, 24, 21, 8, 2, P.fireDeep)
    // Ladder along the top of the tank
    rect(g, 4, 7, 50, 3, 0xb6bec4)
    rect(g, 4, 7, 50, 1, 0xd2d6dc)
    for (let lx = 6; lx < 52; lx += 4) rect(g, lx, 8, 1, 2, 0x6c727a)
    // Flashing light bar on the cab roof
    const flash = Math.floor(timeMs / 140) % 2
    rect(g, 58, 5, 12, 3, 0x1c1c22)
    rect(g, 59, 6, 4, 2, flash === 0 ? P.fireDeep : 0x2a4f7a, flash === 0 ? 1 : 0.45)
    rect(g, 65, 6, 4, 2, flash === 0 ? 0x2a4f7a : P.fireDeep, flash === 0 ? 0.45 : 1)
    // Deck-monitor water cannon on top of the tank, barrel forward over the cab
    rect(g, 30, 4, 8, 4, 0x2c2c34)
    rect(g, 32, 2, 4, 2, 0x2c2c34) // mount
    if (deployed) {
      rect(g, 38, 3, 18, 2, 0x2c2c34) // extended barrel
      rect(g, HOSE_LX, HOSE_LY - 1, 2, 3, 0x44444a) // nozzle tip
    } else {
      rect(g, 38, 4, 10, 1, 0x2c2c34) // stowed barrel
    }
    // Wheels (slight wheel arches above)
    rect(g, 12, 27, 8, 1, P.carArch)
    rect(g, 60, 27, 8, 1, P.carArch)
    circle(g, 16, 32, 4.5, P.carTire)
    circle(g, 16, 32, 3, P.carRimFace)
    circle(g, 16, 32, 1, P.carRim)
    circle(g, 64, 32, 4.5, P.carTire)
    circle(g, 64, 32, 3, P.carRimFace)
    circle(g, 64, 32, 1, P.carRim)
  }

  function drawWater(g: Graphics, timeMs: number, spraying: boolean): void {
    g.clear()
    if (!spraying) return
    // Arc from the nozzle to the car centre in container-local coords. We need
    // the *current* container.x because the target world position is fixed.
    const fromX = HOSE_LX
    const fromY = HOSE_LY
    const toX = TARGET_WX - x
    const toY = TARGET_WY - TRUCK_Y
    const PEAK = 16
    // Continuous thick stream: overlapping translucent halos along the arc
    // with a brighter core on top — reads as a real water jet from across the room.
    const STEPS = 22
    for (let i = 0; i <= STEPS; i++) {
      const t = i / STEPS
      const ax = fromX + (toX - fromX) * t
      const ay = fromY + (toY - fromY) * t - PEAK * Math.sin(Math.PI * t)
      const wig = Math.sin(timeMs / 80 + i * 0.7) * 0.6
      rect(g, Math.round(ax - 1), Math.round(ay + wig - 1), 3, 3, 0x9fd4e8, 0.55)
      rect(g, Math.round(ax), Math.round(ay + wig), 2, 2, 0xdaeefa, 0.85)
    }
    // Bright sparkles racing along the arc.
    for (let i = 0; i < 6; i++) {
      const t = ((timeMs / 110 + i * 0.16) % 1)
      const sx = fromX + (toX - fromX) * t
      const sy = fromY + (toY - fromY) * t - PEAK * Math.sin(Math.PI * t)
      rect(g, Math.round(sx), Math.round(sy), 2, 2, 0xffffff, 0.95)
    }
    // Big splash + sideways spurts at the impact.
    const flick = 0.5 + 0.5 * Math.sin(timeMs / 50)
    const ix = Math.round(toX)
    const iy = Math.round(toY)
    rect(g, ix - 8, iy - 2, 17, 3, 0xdaeefa, 0.7 * flick)
    rect(g, ix - 6, iy - 4, 13, 2, 0xffffff, 0.7)
    rect(g, ix - 10, iy + 1, 21, 2, 0x9fd4e8, 0.5 * flick)
    rect(g, ix - 6, iy + 3, 13, 1, 0xdaeefa, 0.55)
    for (let s = 0; s < 6; s++) {
      const a = (s / 6) * Math.PI * 2
      const r = 5 + (Math.sin(timeMs / 80 + s) * 0.5 + 0.5) * 6
      const sx = ix + Math.cos(a) * r
      const sy = iy + Math.sin(a) * r * 0.6
      rect(g, Math.round(sx), Math.round(sy), 2, 1, 0xdaeefa, 0.6 * flick)
    }
    // Steam rising off the doused fire
    for (let s = 0; s < 4; s++) {
      const st = ((timeMs / 600 + s * 0.25) % 1)
      const sy2 = iy - 6 - Math.round(st * 20)
      const sx2 = ix - 4 + Math.round(Math.sin(timeMs / 350 + s) * 3)
      rect(g, sx2, sy2, 8, 4, 0xe6e6e6, 0.5 * (1 - st))
      rect(g, sx2 + 2, sy2 - 2, 4, 3, 0xeeeeee, 0.35 * (1 - st))
    }
  }

  return {
    container,
    command(mode) {
      if (mode === 'extinguish') {
        // Only kick off a new gag if we're not already in one — re-clicks
        // shouldn't restart mid-spray.
        if (state === 'offscreen') {
          state = 'driving-in'
          elapsed = 0
          x = OFFSCREEN_X
        }
      } else if (mode === 'leave') {
        // Signal to depart — only meaningful while we're actually on scene.
        if (state === 'driving-in' || state === 'spraying') {
          state = 'driving-out'
          elapsed = 0
        }
      } else {
        // 'idle' — hard reset (world teardown).
        state = 'offscreen'
        elapsed = 0
        x = OFFSCREEN_X
        container.alpha = 0
      }
    },
    sprayAmount() {
      return state === 'spraying' ? 1 : 0
    },
    update(dtMs, timeMs) {
      if (state === 'offscreen') {
        container.alpha = 0
        return
      }
      container.alpha = 1
      elapsed += dtMs
      if (state === 'driving-in') {
        const t = Math.min(1, elapsed / DRIVE_IN_MS)
        const eased = 1 - Math.pow(1 - t, 2) // ease-out arrival
        x = OFFSCREEN_X + (PARK_X - OFFSCREEN_X) * eased
        if (t >= 1) {
          state = 'spraying'
          elapsed = 0
        }
      } else if (state === 'spraying') {
        // Parked, blasting water — stays here until commanded to 'leave'.
        x = PARK_X
      } else if (state === 'driving-out') {
        const t = Math.min(1, elapsed / DRIVE_OUT_MS)
        const eased = t * t // ease-in departure
        x = PARK_X + (OFFSCREEN_X - PARK_X) * eased
        if (t >= 1) {
          state = 'offscreen'
          x = OFFSCREEN_X
        }
      }
      // Engine rumble while parked & pumping.
      const rumble = state === 'spraying' ? Math.round(Math.sin(timeMs / 60) * 0.5) : 0
      container.position.set(Math.round(x), TRUCK_Y + rumble)
      drawBody(body, timeMs, state === 'spraying')
      drawWater(water, timeMs, state === 'spraying')
    },
  }
}
